import React, { useState, useEffect } from "react";

// Components
import { Button, Form } from 'react-bulma-components';
import { Thumbnail } from "shared/components/user";
import CompanyDetail from "shared/components/company/CompanyDetail";
import { Check, Phone, Send, Trash2, X } from 'react-feather';

// utils
import { convertObjNullToEmpty, getUserInitials } from "shared/utils";

//actions
import { useAuthUser } from 'shared/state';
import { makeAPIRequest } from "shared/state/fetch";
import { useTokenStatus } from "shared/state/auth/actions";

// Styles
import styles from './ContactDetail.module.scss';


const ContactDetail = ({ contact='', isActive=false, afterSaveContact, onClose }) => {
    const { Input, Field, Control, Label } = Form;
    const { user } = useAuthUser();
    const { handleTokenStatus } = useTokenStatus();
    const [isProcessing, setIsProcessing] = useState(false);
    const [buttonColor, setButtonColor] = useState('');
    const normalizedContact = contact && convertObjNullToEmpty(contact);
    const [updatedContact, setUpdatedContact] = useState(normalizedContact);
    const canEdit = user && user.role !== 'member';

    const initialName = `${contact.name} ${contact.surname}`;
    const initials = getUserInitials(contact.name, contact.surname);

    const handleInputChange = (e) => {
        setUpdatedContact(prevState => ({
           ...prevState,
           [e.target.name]: e.target.value
        }));
    }

    const handleSaveContact = (e) => {
        e.preventDefault();
        setIsProcessing(true);

        const updatedContactObj = {
            ...updatedContact,
            company_id: updatedContact.company.company_id,
            role: 'contact',
            contact_owner: user.id
        };

        makeAPIRequest(`contacts/${updatedContactObj.id}`, 'PUT', updatedContactObj)
            .then(response => {
                const resultType = !response.status && response.result !== false ? 'success' : 'danger';
                afterSaveContact({ shouldFetch: true });
                setIsProcessing(false);
                setButtonColor(resultType);
                response.status && handleTokenStatus(response);
        })
    }

    const handleCancel = (e) => {
        e.preventDefault();
        setUpdatedContact(contact);
        //setUpdatedAddress(contact.address);
        onClose();
    }

    useEffect(() => {
        let timer;
        if(buttonColor !== '') {
            timer = setTimeout(() => {
                setButtonColor('');
            }, 2500);
        }
        return () => clearTimeout(timer);
    }, [buttonColor]);

    return (
        <div className={ styles.wrapper }>
            <div className={ styles.detail }>
                <article className={ styles.inner }>
                    <header className={ styles.header }>
                        <div className={ styles.innerHeader }>
                            <h2>{ initialName }</h2>
                            <span className={ styles.owner }>
                                <span>Contact Owner</span> { updatedContact.contact_owner.name }
                            </span>
                            <div className={ styles.buttons }>
                                <Button.Group style={{ marginTop: '1.5rem'}}>
                                    <Button rounded disabled>
                                        <Send size={20} strokeWidth={1.5} className={ styles.icon } /> Send Email
                                    </Button>
                                    {   // phone button
                                        updatedContact.phone !== '' &&
                                        <Button
                                            href={`tel:${updatedContact.phone}`}
                                            renderAs="a"
                                            rounded>
                                            <Phone size={20} strokeWidth={1.5} className={ styles.icon } />
                                            Call
                                        </Button>
                                    }
                                </Button.Group>
                                <Button
                                    text
                                    rounded
                                    className={ styles.delete }>
                                    <Trash2 size={20} strokeWidth={1.5} className={ styles.icon } />
                                    Delete Contact
                                </Button>
                            </div>
                        </div>
                        <Thumbnail
                            size="large"
                            image={ updatedContact.image }
                            initials={ initials }
                            backgroundColor="rgb(0, 48, 80)" />
                    </header>

                    <div className={ styles.user }>
                        <Field>
                            <Label>Name</Label>
                            <Control>
                                <Input
                                    isStatic={ !canEdit }
                                    name="name"
                                    onChange={ handleInputChange }
                                    required
                                    value={ updatedContact.name }
                                />
                            </Control>
                        </Field>
                        <Field>
                            <Label>Last Name</Label>
                            <Control>
                                <Input
                                    isStatic={ !canEdit }
                                    onChange={ handleInputChange }
                                    name="surname"
                                    required
                                    value={ updatedContact.surname }
                                />
                            </Control>
                        </Field>
                        <Field>
                            <Label>Email</Label>
                            <Control>
                                <Input
                                    isStatic={ !canEdit }
                                    onChange={ handleInputChange }
                                    name="email"
                                    required
                                    value={ updatedContact.email }
                                    />
                            </Control>
                        </Field>
                        <Field>
                            <Label>Phone</Label>
                            <Control>
                                <Input
                                    isStatic={ !canEdit }
                                    onChange={ handleInputChange }
                                    name="phone"
                                    required
                                    value={ updatedContact.phone }
                                    />
                            </Control>
                        </Field>
                        <Field>
                            <Label>Job title</Label>
                            <Control>
                                <Input
                                    isStatic={ !canEdit }
                                    onChange={ handleInputChange }
                                    name="job_title"
                                    value={ updatedContact.job_title }
                                    />
                            </Control>
                        </Field>
                    </div>
                    <section className="mt-6 pb-6">
                        <h3>Location</h3>
                        <div className={ styles.address }>
                            <Field>
                                <Label>City</Label>
                                <Control>
                                    <Input
                                        isStatic={ !canEdit }
                                        name="city"
                                        onChange={ handleInputChange }
                                        type="text"
                                        value={ updatedContact.city }
                                        />
                                </Control>
                            </Field>
                            <Field>
                                <Label>Zip code</Label>
                                <Control>
                                    <Input
                                        isStatic={ !canEdit }
                                        name="zip"
                                        onChange={ handleInputChange }
                                        type="text"
                                        value={ updatedContact.zip }
                                        />
                                </Control>
                            </Field>
                            <Field>
                                <Label>Country</Label>
                                <Control>
                                    <Input
                                        isStatic={ !canEdit }
                                        name="country"
                                        onChange={ handleInputChange }
                                        type="text"
                                        value={ updatedContact.country }
                                        />
                                </Control>
                            </Field>
                        </div>
                    </section>
                    <section className="pb-3">
                        <h3>Company </h3>
                        <CompanyDetail
                            company={ updatedContact.company }
                            viewOnly={ canEdit }
                            withSelect={ !canEdit }
                        />
                    </section>
                    <footer className={ styles.actions }>
                        <Button onClick={ handleCancel }>
                            Cancel
                        </Button>
                        <Button
                            className={ buttonColor === '' ? styles.secondary : '' }
                            color={ buttonColor !== '' ? buttonColor : '' }
                            loading={ isProcessing }
                            onClick={ buttonColor !=='' ? null : handleSaveContact }>
                            { buttonColor === '' && 'Save Changes' }
                            { buttonColor === 'success' &&  <Check /> }
                            { buttonColor === 'danger' &&  <X /> }
                        </Button>
                    </footer>
                </article>
            </div>
        </div>
    )
}

export default ContactDetail;
